import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material'

import { FC, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { ArrowBackIosNew, InfoOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import axios, { AxiosError } from 'axios'
import { StatusCodes } from 'http-status-codes'
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input'
import { useNavigate } from 'react-router-dom'
import { fullName } from '../../domain/Account/AccountDisplay'
import { AdoptersClient } from '../../interactors/clients/AdoptersClient'
import {
  AccountDtoPermission,
  AdopterDto,
  AdopterDtoProfileHasBeenAccepted,
  CreateAdopterBodyDto,
  AnimalAdoptionDtoOfferType,
} from '../../interactors/gen/backendClient'
import { useGlobalSnackbarStore } from '../../store/GlobalSnackBarStore'
import { readableAxiosError } from '../../utils/axios'
import { omitEmptyString } from '../../utils/omitEmptyString'
import { PATHS } from '../PATHS'
import { theme } from '../theme'
import { AdoptionContext as FastLaunchAdoptionContext, FastLaunchProcedureDialog } from './FastLaunchProcedureDialog'
import { useAccountsStore } from '../../store/AccountsStore'
import { ControlledSelectField } from '../common/ControlledSelectField'
import { getReadableAnimalAdoptionOfferType } from '../../domain/Animal/AnimalDisplay'
import { ControlledDateField } from '../common/ControlledDateField'
import { isValidPastDateCoherence } from '../../utils/date/isValidPastDateCoherence'
import { ColoredChipSelectField } from '../common/ColoredChipSelectField'
import { setOpacity } from '../../utils/setOpacity'
import { useStripeConnectStatus } from '../../utils/hooks/useStripeConnectStatus'
import { StripeConnectRequiredStep } from './StripeConnectRequiredStep'

// We need to omit the select and add empty string, if not it does not work
type CreateAdopterFormParams = CreateAdopterBodyDto

type AdoptionIntentFormParams = {
  adoptionType: 'request' | 'past'
  offerType?: AnimalAdoptionDtoOfferType
  priceInEuros?: number
  hasDeposit: boolean
  depositDate?: string
  depositAmount?: number
  depositStatus?: 'true' | 'false'
  prepareDocuments: boolean
}

type AdoptionContext = FastLaunchAdoptionContext

export const AddAdopterScreen: FC = () => {
  const queryParams = new URLSearchParams(location.search)
  const [currentStep, setCurrentStep] = useState<'adoption' | 'info'>('adoption')
  const [isLoading, setIsLoading] = useState(false)
  const globalSnackbarStore = useGlobalSnackbarStore()
  const navigate = useNavigate()
  const accountsStore = useAccountsStore()
  const {
    register: registerAdoption,
    control: adoptionControl,
    handleSubmit: handleAdoptionSubmit,
    formState: { errors: adoptionErrors },
    watch: watchAdoptionForm,
    setValue: setAdoptionValue,
  } = useForm<AdoptionIntentFormParams>({
    defaultValues: {
      adoptionType: 'request',
      hasDeposit: false,
      depositStatus: 'true',
      prepareDocuments: accountsStore.isPremium(),
      offerType: AnimalAdoptionDtoOfferType.HealthCostCompensation,
    },
  })

  const {
    register: registerAdopter,
    control: adopterControl,
    handleSubmit: handleAdopterSubmit,
    reset,
    formState: { errors: adopterErrors },
  } = useForm<CreateAdopterFormParams>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      address: '',
      postalCode: '',
      city: '',
      country: 'France',
      sendForm: false,
    },
    mode: 'onChange',
  })

  const [userWantToAssignPastAdoption, setUserWantToAssignPastAdoption] = useState<boolean>(false)
  const [openSteps, setOpenSteps] = useState(false)
  const [adopter, setAdopter] = useState<AdopterDto | null>(null)
  const [adoptionContext, setAdoptionContext] = useState<AdoptionContext>({
    hasDeposit: false,
    prepareDocuments: accountsStore.isPremium(),
  })

  const sendEmailToAdopter = true

  const [serverError, setServerError] = useState('')

  const isReader = accountsStore.connectedAccount?.permission === AccountDtoPermission.Read
  const isNotAdmin = !accountsStore.isAdmin()
  const editorsCanCreateAdoptions = accountsStore.connectedCustomer?.editorsCanCreateAdoptions === true
  const isPremium = accountsStore.isPremium()
  const { status: stripeStatus, refresh: refreshStripeStatus } = useStripeConnectStatus(
    currentStep === 'info' && !userWantToAssignPastAdoption
  )

  const adoptionType = watchAdoptionForm('adoptionType')
  const hasDeposit = watchAdoptionForm('hasDeposit')
  const offerType = watchAdoptionForm('offerType')
  const isDonation = offerType === AnimalAdoptionDtoOfferType.Donation
  const isAdoptionRequest = adoptionType === 'request'

  useEffect(() => {
    if (!offerType) {
      setAdoptionValue('offerType', AnimalAdoptionDtoOfferType.HealthCostCompensation)
    }
  }, [offerType, setAdoptionValue])

  const handleAdoptionTypeToggle = (newValue: 'request' | 'past') => {
    setAdoptionValue('adoptionType', newValue)
    if (newValue === 'past') {
      setUserWantToAssignPastAdoption(true)
      setCurrentStep('info')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      setUserWantToAssignPastAdoption(false)
      setCurrentStep('adoption')
    }
  }

  useEffect(() => {
    if (isDonation) {
      setAdoptionValue('priceInEuros', 0)
    }
  }, [isDonation, setAdoptionValue])

  useEffect(() => {
    if (!hasDeposit) {
      setAdoptionValue('depositDate', undefined)
      setAdoptionValue('depositAmount', undefined)
      setAdoptionValue('depositStatus', 'true')
    }
  }, [hasDeposit, setAdoptionValue])

  const handleAdoptionStepSubmit = (data: AdoptionIntentFormParams) => {
    const isRequest = data.adoptionType === 'request'
    const isDonationSelection = data.offerType === AnimalAdoptionDtoOfferType.Donation
    const shouldPrepareDocuments = isRequest && isPremium && data.prepareDocuments
    setUserWantToAssignPastAdoption(!isRequest)
    setAdoptionContext({
      offerType: isRequest ? data.offerType : undefined,
      priceInEuros: isRequest ? (isDonationSelection ? 0 : data.priceInEuros) : undefined,
      hasDeposit: isRequest ? data.hasDeposit : false,
      depositDate: isRequest && data.hasDeposit ? data.depositDate : undefined,
      depositAmount: isRequest && data.hasDeposit ? data.depositAmount : undefined,
      depositStatus: isRequest && data.hasDeposit ? (data.depositStatus === 'true' ? true : false) : undefined,
      prepareDocuments: shouldPrepareDocuments,
    })
    setCurrentStep('info')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const onSubmit = async (data: CreateAdopterFormParams) => {
    setIsLoading(true)
    console.log('On submit', data)

    if (!userWantToAssignPastAdoption && stripeStatus !== 'connected') {
      setIsLoading(false)
      return
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataWithoutEmptyString = omitEmptyString(data as any)

    if (userWantToAssignPastAdoption) {
      dataWithoutEmptyString.profileHasBeenAccepted = AdopterDtoProfileHasBeenAccepted.Accepted
    }

    const from = queryParams.get('from')

    await AdoptersClient.createAdopter(dataWithoutEmptyString as CreateAdopterBodyDto)
      .then((account) => {
        console.log('✨ Adopter created!')

        globalSnackbarStore.triggerSuccessMessage(`Adoptant ${fullName(data)} a été créé.`)

        if (!from && !userWantToAssignPastAdoption) {
          setAdopter(account)
          setOpenSteps(true)
          reset()
          return
        }

        if (from && from.length > 0) {
          return navigate(
            `/${from}&adoptant=${account.id}&card=${queryParams.get('card')}&status=${queryParams.get('status')}`
          )
        }
        if (userWantToAssignPastAdoption) {
          return navigate(
            `${PATHS.assignerAnimal.absolute}?adopterId=${
              account.id
            }&type=pastAdoption&sendEmailToAdopter=${sendEmailToAdopter.toString()}`
          )
        }
      })
      .catch((error: Error | AxiosError) => {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === StatusCodes.CONFLICT) {
            setIsLoading(false)
            setServerError('Un utilisateur existe déjà avec cette adresse mail.')
            return
          }
        }

        setServerError(readableAxiosError(error).join(' '))
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const spacingItem = { marginTop: 2 }
  return (
    <>
      <Box sx={{ width: '900px', maxWidth: '90%', marginX: 'auto', marginY: '3rem' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {currentStep === 'info' && (
            <Tooltip title="Revenir au choix du type d'adoption">
              <IconButton
                size="small"
                onClick={() => {
                  setAdoptionValue('adoptionType', 'request')
                  setUserWantToAssignPastAdoption(false)
                  setCurrentStep('adoption')
                }}
              >
                <ArrowBackIosNew fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Typography variant="h4">😇 Nouvelle Adoption</Typography>
        </Box>

        {currentStep === 'adoption' ? (
          <form onSubmit={handleAdoptionSubmit(handleAdoptionStepSubmit)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography fontSize={16} fontWeight={700}>
                Ce nouvel adoptant concerne :<span style={{ color: 'red' }}> *</span>
              </Typography>
              <Tooltip title="L'intérêt d'ajouter une adoption passée est de pouvoir conserver l'historique des adoptions de l'adoptant sur Petso.">
                <InfoOutlined sx={{ color: theme.palette.primary.main }} />
              </Tooltip>
            </Box>
            <Controller
              name="adoptionType"
              control={adoptionControl}
              render={({ field }) => (
                <ToggleButtonGroup
                  exclusive
                  color="info"
                  value={field.value}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      field.onChange(newValue)
                      handleAdoptionTypeToggle(newValue)
                    }
                  }}
                  aria-label="adoption type"
                  fullWidth
                >
                  <ToggleButton value="request" aria-label="Une demande d'adoption">
                    Une demande d&apos;adoption
                  </ToggleButton>
                  <ToggleButton value="past" aria-label="Une adoption passée">
                    Une adoption passée
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            />

            {isAdoptionRequest && (
              <>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5, fontStyle: 'italic' }}>
                  <Typography variant="body2" color="text.primary">
                    👉 Lancez une procédure d&apos;adoption
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    👉 Complétez le suivi de la procédure
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    👑 Premium - générez et envoyez vos documents à partir de vos modèles et validez leur réception
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    👉 Permettez à l&apos;adoptant de régler les frais d&apos;adoption en ligne depuis un espace Petso
                    dédié
                  </Typography>
                </Box>
                {!accountsStore.connectedCustomer?.stripeAccountId && (
                  <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                    Vous devez compléter un formulaire de configuration auprès de notre partenaire Stripe pour autoriser
                    la réception des réglements en ligne.
                  </Typography>
                )}

                <Box sx={{ ...spacingItem }}>
                  <ControlledSelectField
                    control={adoptionControl}
                    error={adoptionErrors.offerType}
                    fieldName="offerType"
                    label="Nature de l'offre"
                    options={Object.values(AnimalAdoptionDtoOfferType).map((field: AnimalAdoptionDtoOfferType) => ({
                      label: getReadableAnimalAdoptionOfferType(field),
                      value: field,
                    }))}
                    requiredRule="Vous devez renseigner la nature de l'offre."
                    size="small"
                  />
                </Box>

                <TextField
                  id="price-input"
                  label="Montant de l'adoption"
                  type="number"
                  inputProps={{
                    step: '0.01',
                    inputMode: 'decimal',
                    pattern: '[0-9]*',
                  }}
                  fullWidth
                  {...registerAdoption('priceInEuros', {
                    valueAsNumber: true,
                    validate: (value) => {
                      if (!isAdoptionRequest || isDonation) return true
                      if (value === undefined || value === null || Number.isNaN(value)) {
                        return "Le montant de l'adoption est requis"
                      }
                      if (value < 0) {
                        return 'Le montant doit être positif'
                      }
                      return true
                    },
                  })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={spacingItem}
                  size="small"
                  disabled={isDonation}
                  error={!!adoptionErrors.priceInEuros}
                  helperText={adoptionErrors.priceInEuros?.message}
                />

                <Box sx={{ ...spacingItem, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography fontWeight={600}>Un acompte a-t-il déjà été réalisé ?</Typography>
                  <Controller
                    name="hasDeposit"
                    control={adoptionControl}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                      />
                    )}
                  />
                </Box>

                {hasDeposit && (
                  <Box
                    sx={{
                      ...spacingItem,
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                      gap: 2,
                    }}
                  >
                    <ControlledDateField
                      control={adoptionControl}
                      error={adoptionErrors.depositDate}
                      fieldName="depositDate"
                      label="Date de l'acompte"
                      requiredRule="Vous devez renseigner la date de l'acompte."
                      size="small"
                      validate={(value) => isValidPastDateCoherence(value) || value === null}
                    />
                    <TextField
                      id="deposit-amount-input"
                      label="Montant de l'acompte"
                      type="number"
                      inputProps={{
                        step: '0.01',
                        inputMode: 'decimal',
                        pattern: '[0-9]*',
                      }}
                      fullWidth
                      {...registerAdoption('depositAmount', {
                        valueAsNumber: true,
                        validate: (value) => {
                          if (!hasDeposit) return true
                          if (value === undefined || value === null || Number.isNaN(value)) {
                            return "Le montant de l'acompte est requis"
                          }
                          if (value < 0) {
                            return 'Le montant doit être positif'
                          }
                          return true
                        },
                      })}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">€</InputAdornment>,
                      }}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      error={!!adoptionErrors.depositAmount}
                      helperText={adoptionErrors.depositAmount?.message}
                    />
                    <ColoredChipSelectField
                      control={adoptionControl}
                      error={adoptionErrors.depositStatus}
                      fieldName="depositStatus"
                      label="État de l'acompte"
                      options={[
                        { label: 'Validé', value: 'true', color: theme.palette.success.main },
                        { label: 'En attente', value: 'false', color: theme.palette.warning.main },
                      ]}
                      requiredRule=""
                      size="small"
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    ...spacingItem,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    padding: 2,
                    borderRadius: 2,
                    backgroundColor: setOpacity(theme.palette.info.main, 0.08),
                    border: `1px solid ${setOpacity(theme.palette.info.main, 0.3)}`,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={700} color="text.primary">
                      Signature en ligne - Générer et envoyer à l&apos;adoptant le certificat et le contrat via Petso
                    </Typography>
                    {watchAdoptionForm('prepareDocuments') ? (
                      <Typography variant="body2" fontStyle="italic" color="text.primary" sx={{ mt: 0.5 }}>
                        Vous pourrez choisir le modèle de document à utiliser pour préparer vos documents dans les
                        prochaines étapes.
                      </Typography>
                    ) : (
                      <Typography variant="body2" fontStyle="italic" color="text.primary" sx={{ mt: 0.5 }}>
                        Les documents seront considérés comme remis en mains propres (aucun envoi via Petso pour cette
                        procédure). Vous pourrez stocker vos documents dans l&apos;espace procédure qui sera créé.
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={isPremium ? '' : 'Réservé au plan Petso Premium'}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Typography fontWeight={700} color="text.primary">
                          {watchAdoptionForm('prepareDocuments') ? 'Activé' : 'Désactivé'}
                        </Typography>
                        <Switch
                          checked={watchAdoptionForm('prepareDocuments')}
                          onChange={(e) => setAdoptionValue('prepareDocuments', e.target.checked)}
                          color="warning"
                          disabled={!isPremium}
                          inputProps={{
                            title: isPremium ? '' : 'Réservé au plan Petso Premium',
                          }}
                        />
                        <span role="img" aria-label="crown">
                          👑
                        </span>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 4 }}>
              <Button variant="outlined" color="primary" onClick={() => navigate(-1)}>
                Retour
              </Button>
              <Button variant="contained" color="primary" type="submit">
                Suivant
              </Button>
            </Box>
          </form>
        ) : !userWantToAssignPastAdoption && stripeStatus !== 'connected' ? (
          stripeStatus === 'loading' ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              padding={3}
              sx={{ width: '100%', height: '100%' }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <StripeConnectRequiredStep
              isAdmin={accountsStore.isAdmin()}
              onClose={() => {
                setAdoptionValue('adoptionType', 'request')
                setUserWantToAssignPastAdoption(false)
                setCurrentStep('adoption')
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
              onRefresh={refreshStripeStatus}
            />
          )
        ) : (
          <form onSubmit={handleAdopterSubmit((data) => onSubmit(data))}>
            <TextField
              id="first-name-input"
              label="Prénom"
              type="text"
              fullWidth
              {...registerAdopter('firstName', { required: 'Le prénom est requis' })}
              sx={spacingItem}
              error={!!adopterErrors.firstName}
              helperText={adopterErrors.firstName?.message}
              required
            />

            <TextField
              id="last-name-input"
              label="Nom de Famille"
              type="text"
              fullWidth
              {...registerAdopter('lastName', { required: 'Le nom est requis' })}
              sx={spacingItem}
              error={!!adopterErrors.lastName}
              helperText={adopterErrors.lastName?.message}
              required
            />

            <TextField
              id="email"
              label="Email"
              type="email"
              fullWidth
              {...registerAdopter('email', {
                required: "L'email est requis.",
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: "L'addresse n'est pas valide.",
                },
              })}
              sx={spacingItem}
              error={!!adopterErrors.email}
              helperText={adopterErrors.email?.message}
              required
            />

            <FormControl fullWidth variant="outlined" sx={spacingItem}>
              <Controller
                name="phoneNumber"
                control={adopterControl}
                rules={{
                  validate: (value: string | undefined) => (!value || matchIsValidTel(value) ? true : 'Tel is invalid'),
                }}
                render={({ field, fieldState }) => (
                  <MuiTelInput
                    {...field}
                    label="Numero de Téléphone"
                    defaultCountry="FR"
                    helperText={fieldState.invalid ? "Le numero de téléphone n'est pas valide" : ''}
                    error={fieldState.invalid}
                  />
                )}
              />
            </FormControl>

            <TextField
              id="address"
              label="Adresse"
              type="text"
              fullWidth
              {...registerAdopter('address')}
              sx={spacingItem}
              error={!!adopterErrors.address}
              helperText={adopterErrors.address?.message}
            />

            <TextField
              id="postalCode"
              label="Code Postal"
              type="text"
              fullWidth
              {...registerAdopter('postalCode')}
              sx={spacingItem}
              error={!!adopterErrors.postalCode}
              helperText={adopterErrors.postalCode?.message}
            />

            <TextField
              id="city"
              label="Ville"
              type="text"
              fullWidth
              {...registerAdopter('city')}
              sx={spacingItem}
              error={!!adopterErrors.city}
              helperText={adopterErrors.city?.message}
            />

            <TextField
              id="country"
              label="Pays"
              type="text"
              fullWidth
              {...registerAdopter('country')}
              sx={spacingItem}
              error={!!adopterErrors.country}
              helperText={adopterErrors.country?.message}
            />

            {serverError && (
              <Box sx={spacingItem}>
                <Typography color="error">{serverError}</Typography>
              </Box>
            )}

            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', ...spacingItem }}>
              <Tooltip
                title={
                  isNotAdmin && !editorsCanCreateAdoptions ? `L'administrateur ne vous a pas autorisé cet accès` : ``
                }
              >
                <span style={{ width: '100%' }}>
                  <LoadingButton
                    variant="contained"
                    sx={{ width: '100%' }}
                    type="submit"
                    loading={isLoading}
                    disabled={isReader || (isNotAdmin && !editorsCanCreateAdoptions)}
                  >
                    Ajouter
                  </LoadingButton>
                </span>
              </Tooltip>
            </Box>
          </form>
        )}
      </Box>
      <FastLaunchProcedureDialog
        open={openSteps}
        adopter={adopter}
        setOpen={setOpenSteps}
        sendEmailToAdopter={sendEmailToAdopter}
        adoptionContext={adoptionContext}
      />
    </>
  )
}
